import React, { useState } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, CheckCircle2, ChevronDown, LineChart } from 'lucide-react'
import DashboardCard, { CardSlot } from '@/components/dashboard/DashboardCard'
import SectionHeading from '@/components/dashboard/SectionHeading'
import { ATTENTION_ICON, TONE_STYLE, cardCovers } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketAttention, MarketAttentionItem } from '@/types/market'

/**
 * "Worth a look": runs of nights where the comparison says something, ranked by the server. One card
 * per run of nights: when two findings cover the same nights the more specific one leads and the
 * other rides inside the card as a supporting line, with its link as the card's second action.
 * Facts and links only: the page never recommends a number.
 */

/** Cards shown on a phone before "Show all". */
const PHONE_CARDS = 3

const linkClass =
  'inline-flex h-8 cursor-pointer items-center gap-1 rounded-lg px-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

function AttentionCard({
  item,
  active,
  onShow,
}: {
  item: MarketAttentionItem
  active: boolean
  onShow: (date: string) => void
}) {
  const Icon = ATTENTION_ICON[item.kind]
  const tone = TONE_STYLE[item.tone]
  return (
    <DashboardCard
      accent={tone.accent}
      className={cn(active && 'ring-2 ring-primary/30')}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={() => onShow(item.startDate)}
            className={cn(linkClass, 'gap-1.5 font-medium text-muted-foreground hover:bg-muted hover:text-foreground')}
          >
            <LineChart className="h-3.5 w-3.5" />
            Show on chart
          </button>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
            {item.secondaryCta && (
              <Link
                href={item.secondaryCta.href}
                className={cn(linkClass, 'font-medium text-muted-foreground hover:bg-muted hover:text-foreground')}
              >
                {item.secondaryCta.label}
              </Link>
            )}
            {item.cta && (
              <Link href={item.cta.href} className={cn(linkClass, 'font-semibold text-primary hover:bg-primary/5')}>
                {item.cta.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tone.soft, tone.icon)}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className={cn('text-[11px] font-semibold uppercase tracking-[0.1em]', tone.text)}>{item.rangeLabel}</p>
          <p className="mt-1 text-sm font-semibold leading-snug text-foreground">{item.headline}</p>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{item.detail}</p>
          {item.supporting.length > 0 && (
            <ul className="mt-3 space-y-1.5 border-t border-border/40 pt-3" aria-label="Also on these nights">
              {item.supporting.map((support) => {
                const SupportIcon = ATTENTION_ICON[support.kind]
                return (
                  <li
                    key={`${support.kind}-${support.startDate}`}
                    className="flex items-start gap-2 text-xs leading-relaxed text-foreground/80"
                  >
                    <SupportIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" />
                    <span>{support.sentence}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </DashboardCard>
  )
}

export default function AttentionList({
  attention,
  selectedDate,
  onShow,
}: {
  attention: MarketAttention
  selectedDate: string | null
  onShow: (date: string) => void
}) {
  const { items, totalCount, sentence } = attention
  const [showAll, setShowAll] = useState(false)
  const folded = items.length > PHONE_CARDS && !showAll

  return (
    <section aria-labelledby="worth-a-look" id="worth-a-look-section">
      <SectionHeading
        id="worth-a-look"
        title="Worth a look"
        subtitle={items.length > 0 ? sentence : undefined}
        help={{
          content:
            'Runs of nights where your price and the compset tell a story worth checking. Each one links to the night on your Prices calendar. Nothing here changes a price.',
        }}
      >
        {totalCount > items.length && (
          <span className="text-xs text-muted-foreground">
            Showing {items.length} of {totalCount}
          </span>
        )}
      </SectionHeading>

      {items.length === 0 ? (
        sentence && (
          <div className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-white px-5 py-4 text-sm text-foreground shadow-sm">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
            {sentence}
          </div>
        )
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item, i) => (
              <CardSlot
                key={`${item.kind}-${item.startDate}`}
                delay={i * 50}
                className={cn(folded && i >= PHONE_CARDS && 'hidden md:block')}
              >
                <AttentionCard
                  item={item}
                  active={selectedDate != null && cardCovers(item, selectedDate)}
                  onShow={onShow}
                />
              </CardSlot>
            ))}
          </div>
          {items.length > PHONE_CARDS && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={!folded}
              className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1 rounded-lg border border-border/60 bg-white text-xs font-medium text-primary shadow-sm hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 md:hidden"
            >
              {folded ? `Show all ${items.length}` : 'Show fewer'}
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', !folded && 'rotate-180')} />
            </button>
          )}
        </>
      )}
    </section>
  )
}
